import { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Progress,
  Image,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { Clock, CheckCircle, BarChart2 } from 'lucide-react';
import { useVotePoll } from '@/api/mutations/useSocialMutations';
import type { Poll } from '@/types/social';

interface PollCardProps {
  poll: Poll;
  urlKey: string;
}

export function PollCard({ poll, urlKey }: PollCardProps) {
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(poll.userVote);
  const [hasVoted, setHasVoted] = useState(!!poll.userVote);
  const votePoll = useVotePoll();
  const toast = useToast();

  // Calculate time remaining
  const getTimeRemaining = () => {
    const now = new Date();
    const expires = new Date(poll.expiresAt);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return 'Ended';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };

  const handleVote = async (optionId: number) => {
    if (!poll.isActive) {
      toast({
        title: 'Poll has ended',
        status: 'info',
        duration: 2000,
      });
      return;
    }

    try {
      await votePoll.mutateAsync({ urlKey, optionId });
      setSelectedOptionId(optionId);
      setHasVoted(true);
      toast({
        title: 'Vote recorded!',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Failed to vote',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const showResults = hasVoted || !poll.isActive;

  return (
    <Box
      bg="var(--color-gray-50)"
      borderRadius="var(--r-card)"
      p={4}
      border="1px solid"
      borderColor="var(--color-gray-200)"
    >
      {/* Poll Header */}
      <HStack justify="space-between" mb={3}>
        <HStack spacing={2}>
          <BarChart2 size={18} color="var(--color-primary)" />
          <Text fontWeight="semibold" fontSize="md">
            {poll.question}
          </Text>
        </HStack>
        <Badge
          bg={poll.isActive ? 'green.100' : 'gray.100'}
          color={poll.isActive ? 'green.700' : 'gray.700'}
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Clock size={12} />
          {getTimeRemaining()}
        </Badge>
      </HStack>

      {/* Poll Options */}
      <VStack spacing={3} align="stretch">
        {poll.options.map((option) => {
          const isSelected = selectedOptionId === option.id;
          const isWinning = showResults && option.voteCount === Math.max(...poll.options.map(o => o.voteCount));

          return (
            <Box
              key={option.id}
              position="relative"
              borderRadius="var(--r-card-compact)"
              overflow="hidden"
              border="2px solid"
              borderColor={isSelected ? 'var(--color-primary)' : 'var(--color-gray-200)'}
              transition="all 0.2s"
              cursor={poll.isActive && !hasVoted ? 'pointer' : 'default'}
              onClick={() => {
                if (poll.isActive && !hasVoted) {
                  handleVote(option.id);
                }
              }}
              _hover={
                poll.isActive && !hasVoted
                  ? { borderColor: 'var(--color-primary)', transform: 'scale(1.01)' }
                  : {}
              }
            >
              {/* Progress Bar Background (only show after voting) */}
              {showResults && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  height="100%"
                  width={`${option.votePercentage}%`}
                  bg={isWinning ? 'var(--color-primary-alpha-20)' : 'var(--color-gray-100)'}
                  transition="width 0.5s ease-out"
                  zIndex={0}
                />
              )}

              {/* Content */}
              <HStack
                p={3}
                position="relative"
                zIndex={1}
                justify="space-between"
              >
                <HStack spacing={3}>
                  {/* Restaurant Image */}
                  {option.restaurant.imageUrl ? (
                    <Image
                      src={option.restaurant.imageUrl}
                      alt={option.restaurant.name}
                      boxSize="48px"
                      borderRadius="var(--r-inner)"
                      objectFit="cover"
                    />
                  ) : (
                    <Box
                      boxSize="48px"
                      borderRadius="var(--r-inner)"
                      bg="var(--color-gray-200)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="lg">🍽️</Text>
                    </Box>
                  )}

                  {/* Restaurant Info */}
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" fontSize="sm">
                      {option.restaurant.name}
                    </Text>
                    {option.restaurant.cuisines && option.restaurant.cuisines.length > 0 && (
                      <Text fontSize="xs" color="var(--color-gray-500)">
                        {option.restaurant.cuisines.slice(0, 2).join(' • ')}
                      </Text>
                    )}
                  </VStack>
                </HStack>

                {/* Vote Info / Check */}
                <HStack spacing={2}>
                  {showResults && (
                    <Text
                      fontWeight={isWinning ? 'bold' : 'medium'}
                      fontSize="sm"
                      color={isWinning ? 'var(--color-primary)' : 'var(--color-gray-600)'}
                    >
                      {option.votePercentage}%
                    </Text>
                  )}
                  {isSelected && (
                    <CheckCircle
                      size={20}
                      color="var(--color-primary)"
                      fill="var(--color-primary)"
                      strokeWidth={0}
                    />
                  )}
                </HStack>
              </HStack>
            </Box>
          );
        })}
      </VStack>

      {/* Total Votes */}
      <HStack justify="center" mt={3}>
        <Text fontSize="xs" color="var(--color-gray-500)">
          {poll.totalVotes} {poll.totalVotes === 1 ? 'vote' : 'votes'}
        </Text>
      </HStack>
    </Box>
  );
}
